import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, teamLeaderNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Users, 
  CheckCircle2,
  Clock
} from "lucide-react";
import ProjectProgressFiles from "@/components/ProjectProgressFiles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamLeaderProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectRes, assignmentsRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/assignments`, { params: { project_id: projectId } }),
        ]);
        setProject(projectRes.data);
        setAssignment(assignmentsRes.data[0] || null);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const handleStartWork = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}/status`, null, {
        params: { status: "in_progress" }
      });
      if (assignment) {
        await axios.put(`${API}/assignments/${assignment.assignment_id}/status`, null, {
          params: { status: "in_progress" }
        });
      }
      toast.success("Work started!");
      
      // Refresh
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
    } catch (error) {
      toast.error("Failed to start work");
    }
  };

  const handleCompleteWork = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}/status`, null, {
        params: { status: "completed" }
      });
      if (assignment) {
        await axios.put(`${API}/assignments/${assignment.assignment_id}/status`, null, {
          params: { status: "completed" }
        });
      }
      toast.success("Project marked as completed!");
      
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
    } catch (error) {
      toast.error("Failed to complete project");
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={teamLeaderNavItems} title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={teamLeaderNavItems} title="Project Details">
      <div className="space-y-6" data-testid="teamleader-project-detail">
        <Button
          variant="ghost"
          onClick={() => navigate("/team-leader")}
          className="text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Button>

        {/* Project Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-['Unbounded'] font-bold text-white mb-2">
              {project?.title}
            </h1>
            <p className="text-[#94A3B8]">{project?.description}</p>
          </div>

          <div className="flex gap-3">
            {project?.status === "scheduled" && (
              <Button
                onClick={handleStartWork}
                className="bg-[#FF5F1F] text-white hover:bg-[#FF5F1F]/80"
                data-testid="start-work-btn"
              >
                <Clock className="w-5 h-5 mr-2" />
                Start Work
              </Button>
            )}
            {project?.status === "in_progress" && (
              <Button
                onClick={handleCompleteWork}
                className="bg-green-500 text-white hover:bg-green-600"
                data-testid="complete-work-btn"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Mark Complete
              </Button>
            )}
          </div>
        </div>

        {/* Project Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                <MapPin className="w-5 h-5 text-[#23F7DD]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8]">Location</p>
                <p className="text-white font-medium">{project?.location_address}</p>
                <p className="text-sm text-[#94A3B8]">{project?.location_city}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                <Calendar className="w-5 h-5 text-[#23F7DD]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8]">Schedule</p>
                <p className="text-white font-medium">{project?.start_date || "TBD"}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                <Users className="w-5 h-5 text-[#23F7DD]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8]">Crew</p>
                <p className="text-white font-medium">{assignment?.worker_ids?.length || 0} workers</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8]">Status</p>
                <p className="text-white font-medium capitalize">{project?.status?.replace("_", " ")}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Safety Notes */}
        {project?.safety_notes && (
          <Card className="bg-[#0A0A0A] border-[#FF5F1F]/30">
            <CardHeader>
              <CardTitle className="text-[#FF5F1F] font-['Unbounded']">Safety Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white">{project.safety_notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Scope Details */}
        {project?.scope_details && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-['Unbounded']">Scope Details</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8]">{project.scope_details}</p>
            </CardContent>
          </Card>
        )}

        {/* Project Progress & Files */}
        <ProjectProgressFiles 
          projectId={projectId} 
          projectTitle={project?.title || "Project"}
          canEdit={true}
        />
      </div>
    </DashboardLayout>
  );
}
